import {
	type LoaderFunctionArgs,
	defer,
	type MetaFunction,
	redirect,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import NFTProfile from '#app/components/nftProfile/nftProfile.tsx'
import {
	AttestationType,
	MetadataProperties,
	type nft,
	type sale,
} from '#types/loreTypes.tsx'
import { GeneralErrorBoundary } from '#app/components/common/errorBoundary'
import generateNFTProfileMeta from '#app/utils/meta/nftProfileMeta'
import NftProfileRequests from '#app/utils/loaderRequests/nftProfileRequests'
import {
	type DynamicWallet,
	getDomainUrl,
	getSigningContextForSigningUI,
} from '#app/utils/misc.server'
import { isValidChain } from '#app/utils/misc'
import { useState } from 'react'

export const handle = {
	scripts: () => [{ src: 'https://www.instagram.com/embed.js', preload: true }],
}

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	location,
}) => {
	const nft = data?.nft
	const origin = data?.origin
	const { pathname } = location
	// @ts-ignore
	const metaPayload = generateNFTProfileMeta({ origin, path: pathname, nft })
	return metaPayload
}

export const loader = async ({
	params,
	context,
	request,
}: LoaderFunctionArgs) => {
	const origin = getDomainUrl(request)
	let signModalParams: {
		userWallets: { count: number; wallets: DynamicWallet[] } | undefined
		isOpen: boolean
		templateFromURL: string | undefined
		cidMetadata: AttestationType | undefined
		matchingAddress: string | undefined
		isValidSign: boolean
		pendingPinFromURL:
			| { template: string; formData: MetadataProperties }
			| undefined
	} = {
		userWallets: undefined,
		matchingAddress: undefined,
		isValidSign: false,
		templateFromURL: undefined,
		cidMetadata: undefined,
		isOpen: false,
		pendingPinFromURL: undefined,
	}
	try {
		signModalParams = await getSigningContextForSigningUI(request)
	} catch (e) {
		console.error('ERROR loading SignModal Params', e)
	}
	try {
		if (
			typeof params.chain === 'string' &&
			params.chain !== 'undefined' &&
			typeof params.contractAddress === 'string' &&
			typeof params.tokenId === 'string'
		) {
			if (!isValidChain(params.chain)) {
				return redirect(`/unsupportedChain/${params.chain}`)
			}
			const fetchedData = await NftProfileRequests({
				contractAddress: params.contractAddress,
				tokenId: params.tokenId,
				chain: params.chain,
				request,
			})

			return defer({ ...fetchedData, signModalParams, origin })
		} else {
			console.log('NFT PROFILE REQ NOT FETCHING', { params })
		}
		return {
			collection: null,
			nft: null,
			nftCollectionInfo: undefined,
			salesData: null,
			ownersData: null,
			attestations: [],
			contractAddress: null,
			tokenId: null,
			nftSalesPageKey: null,
			origin,
			atomicLoreUrl: new URL(request.url).origin,
			signModalParams: signModalParams,
		}
	} catch (e) {
		//NFT data could not be retrieved from Alchemy
		console.error(e)
		return {
			collection: null,
			nft: null,
			nftCollectionInfo: undefined,
			salesData: null,
			ownersData: null,
			attestations: [],
			contractAddress: null,
			tokenId: null,
			nftSalesPageKey: null,
			origin,
			atomicLoreUrl: new URL(request.url).origin,
			signModalParams: signModalParams,
		}
	}
}

export default function nftPage() {
	const {
		collection,
		nft,
		nftCollectionInfo,
		attestations,
		salesData,
		ownersData,
		contractAddress,
		tokenId,
		nftSalesPageKey,
		origin,
		atomicLoreUrl,
		signModalParams,
	} = useLoaderData<{
		collection: AttestationType
		nft: nft
		nftCollectionInfo:
			| {
					name: string
					artist: { name: string; verified: boolean; src: undefined }
			  }
			| undefined
		attestations: AttestationType[]
		salesData: { sales: sale[]; salesPageKey: string | null }
		ownersData: string[]
		contractAddress: string
		tokenId: string
		nftSalesPageKey: string
		origin: string
		atomicLoreUrl: string
		signModalParams: {
			isOpen: boolean
			templateFromURL: string
			cidMetadata: AttestationType
			matchingAddress: boolean
			isValidSign: boolean
			pendingPinFromURL: { template: string; formData: MetadataProperties }
		}
	}>()
	const [clientAttestations, setClientAttestations] = useState(attestations)
	const [isLoading, setIsLoading] = useState(false)
	const handleSigning = async () => {
		await loadAttestations()
	}

	const loadAttestations = async () => {
		if (isLoading) return
		setIsLoading(true)
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const response = await fetch(
				`/nftAttestationsAPI?chain=${nft.chain}&contractAddress=${nft.contract.address}&tokenId=${nft.tokenId}`,
				options,
			)
			const attestationsResponse = (await response.json()) as {
				mintedMetadata: AttestationType[]
				totalMetadata: number
			}
			if (attestationsResponse.mintedMetadata) {
				// @ts-ignore
				setClientAttestations(attestationsResponse.mintedMetadata)
			}
		} catch (error) {
			console.error('ERROR LOADING NEW ATTESTATIONS', error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<NFTProfile
			collection={collection}
			nft={nft}
			nftCollectionInfo={nftCollectionInfo}
			mintedMetadata={clientAttestations}
			salesData={{ sales: [], salesPageKey: null }}
			ownersData={ownersData}
			contractAddress={contractAddress}
			tokenId={tokenId}
			nftSalesPageKey={nftSalesPageKey}
			origin={origin}
			atomicLoreUrl={atomicLoreUrl}
			signModalParams={signModalParams}
			handleRevalidation={handleSigning}
		/>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
